import React, { useEffect, useState } from "react";
import Layout from "../shared/Layout";
import { useShowTempMessage } from "../shared/hooks/useUtilityContext";
import { logAnalyticsEvent } from "../engagement/Amplitude";
import { getGeminiJson } from "../ai/GeminiUtils";
import { PiX } from "react-icons/pi";

interface Field {
  name: string;
  type: "string" | "number" | "boolean";
}

export default function JsonStringMaker() {
  const [fields, setFields] = useState<Field[]>(
    localStorage.getItem("fields")
      ? JSON.parse(localStorage.getItem("fields")!)
      : [{ name: "", type: "string" }]
  );
  const [jsonString, setJsonString] = useState<string>("");
  const [jsonResponse, setJsonResponse] = useState<string>("");
  const [jsonObject, setJsonObject] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [prompt, setPrompt] = useState<string>(
    localStorage.getItem("json_prompt") || ""
  );
  const showTempMessage = useShowTempMessage();

  useEffect(() => {
    localStorage.setItem("json_prompt", prompt);
  }, [prompt]);

  useEffect(() => {
    localStorage.setItem("fields", JSON.stringify(fields));
  }, [fields]);

  useEffect(() => {
    generateJsonString(fields);
  }, []);

  useEffect(() => {
    if (jsonResponse) {
      try {
        console.log("jsonResponse:", jsonResponse);
        setJsonObject(JSON.parse(jsonResponse) as any);
      } catch (error) {
        console.log(error);
      }
    }
  }, [jsonResponse]);

  const handleFieldChange = (index: number, field: Field) => {
    const newFields = [...fields];
    newFields[index] = field;
    setFields(newFields);
    generateJsonString(newFields);
  };

  const handleAddField = () => {
    setFields([...fields, { name: "", type: "string" }]);
  };

  const generateJsonString = (fields: Field[]) => {
    const properties: { [key: string]: { type: string } } = fields.reduce(
      (acc, field) => {
        if (field.name) {
          acc[field.name] = { type: field.type };
        }
        return acc;
      },
      {} as { [key: string]: { type: string } } // Add index signature to the initial value
    );

    const jsonDefinition = {
      type: "object",
      properties,
    };

    const escapedJsonString = JSON.stringify(jsonDefinition)
      .replace(/"/g, '\\"')
      .replace(/\{/g, "\\{")
      .replace(/\}/g, "\\}");

    setJsonString(`${escapedJsonString}`);
  };

  const fullPrompt = `${prompt}. Use this JSON schema: ${jsonString}`;

  return (
    <Layout>
      <div className="prose p-8">
        <h1>Gemini Pro 1.5 JSON String Maker</h1>
        <span>
          <a href="https://ai.google.dev/gemini-api/docs/api-overview#json">
            Gemini Pro 1.5 can return JSON formatted data.
          </a>{" "}
          <span>
            Use this tool to generate a prompt with the appropriate JSON
            definition string for your data.
          </span>
        </span>
        <h2>Prompt</h2>
        <textarea
          placeholder="Prompt Text"
          className="textarea textarea-bordered w-full text-lg"
          rows={10}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        ></textarea>
        <h2>JSON Fields</h2>
        <div>
          {fields.map((field, index) => (
            <div
              key={index}
              className="mb-2 flex flex-row gap-2 items-center"
            >
              <input
                type="text"
                placeholder="Field Name"
                className="input input-bordered"
                value={field.name}
                onChange={(e) => {
                  // Restrict input to 50 characters, no new lines
                  if (e.target.value.length > 50) {
                    return;
                  }
                  if (e.target.value.includes("\n")) {
                    return;
                  }
                  handleFieldChange(index, { ...field, name: e.target.value });
                }}
              />
              <select
                title="Field Type"
                className="select select-bordered w-48 mr-2"
                value={field.type}
                onChange={(e) =>
                  handleFieldChange(index, {
                    ...field,
                    type: e.target.value as Field["type"],
                  })
                }
              >
                <option value="string">String</option>
                <option value="number">Number</option>
                <option value="boolean">Boolean</option>
              </select>
              <div
                className="btn "
                onClick={() => {
                  const newFields = [...fields];
                  newFields.splice(index, 1);
                  setFields(newFields);
                  generateJsonString(newFields);
                }}
              >
                <PiX />
              </div>
            </div>
          ))}
          <div className="flex flex-row gap-4 mt-4">
            <button
              className="btn "
              onClick={handleAddField}
            >
              Add Field
            </button>
            <button
              className="btn btn-outline"
              onClick={() => {
                setFields([{ name: "", type: "string" }]);
                generateJsonString([{ name: "", type: "string" }]);
              }}
            >
              Clear Fields
            </button>
          </div>
        </div>
        <div>
          <h2>Prompt with JSON Definition:</h2>
          <pre className="whitespace-pre-wrap">{fullPrompt}</pre>
        </div>
        <div className="flex flex-row gap-4">
          <button
            className="btn btn-primary"
            disabled={!jsonString}
            onClick={async () => {
              logAnalyticsEvent("Test JSON Definition");
              setLoading(true);
              const json = await getGeminiJson(fullPrompt);
              setJsonResponse(json.trim());
              setLoading(false);
            }}
          >
            Test
          </button>
          <button
            className="btn btn-secondary"
            disabled={!jsonString}
            onClick={() => {
              logAnalyticsEvent("Copied JSON Definition");
              navigator.clipboard.writeText(fullPrompt);
              showTempMessage("JSON Definition copied to clipboard!");
            }}
          >
            Copy
          </button>
          {loading && <div className="loading loading-dots"></div>}
        </div>
        {jsonResponse && jsonObject && (
          <div>
            <h2>Test Response:</h2>
            <pre className="whitespace-pre-wrap">{jsonResponse}</pre>
            <div>
              {fields.map((field, index) => {
                return (
                  <div className="flex gap-4 items-center">
                    <div className="font-bold">{field.name}: </div>
                    <div>{jsonObject[field.name]}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
